import { PlusIcon } from '@heroicons/react/24/outline';
import type { Model, ModelProvider } from '../../types';
import { platformKeyState, platformKeySummary } from './ProviderRow';
import { hasPlatformKey, plural } from './modelsPageData';

/**
 * The Models page's left rail (task 1167): every Model Provider with a status
 * dot, its key state in one line, its Model count and the platform-default
 * mark. A provider with no key, or disabled, is dimmed. Selecting one shows its
 * card and its Models; Add provider sits at the foot.
 */

interface ProviderRailProps {
  providers: ModelProvider[];
  modelsByProvider: Map<string, Model[]>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  canManage: boolean;
  onAddProvider: () => void;
}

function dotClass(provider: ModelProvider): string {
  if (!provider.enabled) return 'bg-gray-300 dark:bg-gray-600';
  if (platformKeyState(provider).verification === 'failed') return 'bg-red-500';
  return 'bg-green-500';
}

export function ProviderRail({ providers, modelsByProvider, selectedId, onSelect, canManage, onAddProvider }: ProviderRailProps) {
  return (
    <nav aria-label="Model Providers" data-testid="models-provider-rail" className="space-y-1">
      <p className="px-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Providers {providers.length}
      </p>
      {providers.length === 0 && (
        <p className="px-2 text-sm text-gray-500 dark:text-gray-400">No Model Providers.</p>
      )}
      {providers.map((provider) => {
        const selected = provider.id === selectedId;
        const dimmed = !provider.enabled || !hasPlatformKey(provider);
        const count = modelsByProvider.get(provider.id)?.length ?? 0;
        return (
          <button
            key={provider.id}
            type="button"
            onClick={() => onSelect(provider.id)}
            aria-current={selected ? 'true' : undefined}
            data-testid={`models-rail-provider-${provider.key}`}
            data-dimmed={dimmed ? 'true' : 'false'}
            className={`flex w-full items-start gap-2 rounded-md px-2 py-2 text-left ${
              selected
                ? 'bg-indigo-50 ring-1 ring-indigo-200 dark:bg-indigo-900/30 dark:ring-indigo-800'
                : 'hover:bg-gray-50 dark:hover:bg-gray-800'
            } ${dimmed ? 'opacity-60' : ''}`}
          >
            <span aria-hidden="true" className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${dotClass(provider)}`} />
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{provider.displayName}</span>
                {provider.isPlatformDefault && (
                  <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
                    Default
                  </span>
                )}
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400" data-testid={`models-rail-summary-${provider.key}`}>
                {platformKeySummary(provider)} · {plural(count, 'model', 'models')}
                {provider.enabled ? '' : ' · disabled'}
              </span>
            </span>
          </button>
        );
      })}
      {canManage && (
        <button
          type="button"
          onClick={onAddProvider}
          data-testid="models-rail-add-provider"
          className="mt-2 flex w-full items-center gap-1.5 rounded-md px-2 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20"
        >
          <PlusIcon className="h-4 w-4" />
          Add provider
        </button>
      )}
    </nav>
  );
}

export default ProviderRail;
